import { Select } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EntityOption, PreviewEntityProvider } from '../types';
import { AsyncEntitySelect } from './AsyncEntitySelect';

interface EntitySelectorProps {
  provider: PreviewEntityProvider;
  selectedOption: EntityOption | null;
  onSelect: ( option: EntityOption | null ) => void;
  ruleCategories?: string[];
}

export const EntitySelector: React.FC<EntitySelectorProps> = ({
  provider,
  selectedOption,
  onSelect,
  ruleCategories
}) => {
  const { t } = useTranslation( 'rules' );

  // Check if selector should show based on rule categories
  if ( provider.shouldShow && !provider.shouldShow( ruleCategories || [])) {
    return null;
  }

  // Static options mode
  if ( provider.mode === 'static-options' ) {
    return (
      <Select
        label={t( 'preview_entity_selector_label', 'Select entity for preview' )}
        placeholder={t( 'preview_entity_selector_placeholder', 'Choose an entity' )}
        data={provider.staticOptions?.map( opt => ({
          value: opt.id,
          label: opt.label
        })) || []}
        value={selectedOption?.id || null}
        onChange={( value ) => {
          const option = provider.staticOptions?.find( o => o.id === value );
          onSelect( option || null );
        }}
        searchable
        clearable
        radius="xs"
        size="sm"
      />
    );
  }

  // Async search mode
  if ( provider.mode === 'async-search' && provider.asyncFetch ) {
    return (
      <AsyncEntitySelect
        provider={provider.asyncFetch}
        selectedOption={selectedOption}
        onSelect={onSelect}
      />
    );
  }

  return null;
};
